import React from 'react';
import { Fade } from 'react-awesome-reveal';
import { FaGithub, FaExternalLinkAlt, FaRobot } from 'react-icons/fa';

const ProjectsPage = () => {
    const projects = [
        {
            name: "VidSense AI",
            thumbnail: "/assets/vidsense-thumbnail.png",
            description: [
                "Production-grade RAG system for conversational Q&A over YouTube videos with 40-50% reduction in hallucinations.",
                "Features transcript ingestion, semantic chunking, FAISS similarity search, and multi-turn reasoning pipeline.",
                "Implements LLMOps with RAGAS evaluation tracking context relevance, faithfulness, and answer relevancy."
            ],
            tech: ["Python", "LangChain", "FAISS", "Hugging Face", "FastAPI", "RAGAS"],
            github: "https://github.com/garvit-010/VidSense-AI",
            demo: "https://drive.google.com/file/d/1zWL_7J6eRFZOCl56Q3OHiUoSFndeo3cX/view"
        },
        {
            name: "Creator Catalyst",
            thumbnail: "/assets/creator-catalyst-thumbnail.png",
            description: [
                "AI content automation system that repurposes long-form videos into multiple publish-ready formats.",
                "Won 1st Place at AI Demos Hackathon, reducing creator workload by ~80% and time-to-publish from hours to minutes.",
                "Features fact-grounding, engagement scoring, and multi-platform optimization with Gemini video analysis."
            ],
            tech: ["Python", "Streamlit", "Google Gemini", "FFmpeg", "SQLite"],
            github: "https://github.com/garvit-010/Creator-Catalyst",
            demo: null
        },
        {
            name: "MediAssist",
            thumbnail: "/assets/mediassist-thumbnail.png",
            description: [
                "AI-powered clinical documentation system bridging patient-doctor communication with dual-view generation.",
                "Generates patient-friendly summaries (Hindi/English) and structured SOAP notes for clinical records.",
                "Features role-based access control, local AI privacy with Llama 3, and PostgreSQL backend."
            ],
            tech: ["Python", "Flask", "Llama 3", "PostgreSQL", "Ollama"],
            github: "https://github.com/garvit-010/MediAssist",
            demo: null
        },
        {
            name: "Driver Drowsiness Detection",
            thumbnail: "/assets/drowsiness-thumbnail.png",
            description: [
                "Real-time driver drowsiness detection using ResNet-18 with live webcam inference.",
                "Computer vision pipeline with face detection, temporal tracking, and alert triggering.",
                "Edge and IoT-ready system for driving and industrial safety use cases."
            ],
            tech: ["Python", "PyTorch", "ResNet-18", "OpenCV", "Computer Vision"],
            github: "https://github.com/garvit-010/Driver-Drowsiness-Detection",
            demo: null
        }
    ];

    const automations = [
        {
            name: "UX Audit & Heuristic Review Agent",
            thumbnail: "/assets/uxauditthumbnail.png",
            description: [
                "Automated UX audit workflow combining HTML analysis, Google Analytics data, and AI-powered heuristic evaluation.",
                "Evaluates websites against 8 core UX principles including clarity, accessibility, and cognitive load.",
                "Generates comprehensive reports with severity-based findings and prioritized recommendations."
            ],
            tech: ["n8n", "Claude AI", "Google Analytics", "JavaScript"],
            github: "https://github.com/garvit-010/UX-Audit-and-Heuristic-Review-Automation",
            demo: null
        },
        {
            name: "Storyboarding & Script Agent",
            thumbnail: "/assets/storyboardingthumbnail.png",
            description: [
                "Transforms client meeting transcripts into production-ready video storyboards with Figma-ready specs.",
                "Generates scene-by-scene breakdowns with UI layouts, animations, and realistic sample data.",
                "Includes quality assurance scoring and detailed feedback for motion design teams."
            ],
            tech: ["n8n", "OpenAI GPT-4", "Figma", "JavaScript"],
            github: "https://github.com/garvit-010/Storyboarding-and-Scripting-Agent",
            demo: null
        },
        {
            name: "Web Search Auto-Blogger",
            thumbnail: "/assets/websearchbloggerthumbnail.png",
            description: [
                "End-to-end AI blog automation pipeline with deep web research via Tavily and SEO optimization.",
                "Generates long-form, production-ready blog posts and publishes directly to Sanity CMS.",
                "Creates professional hero images using Google Gemini for each generated blog post."
            ],
            tech: ["n8n", "OpenAI", "Tavily", "Sanity CMS", "Gemini"],
            github: "https://github.com/garvit-010/Web-Search-Auto-Blogger",
            demo: null
        }
    ];

    const renderProjectCard = (project, index) => (
        <div key={index} className="project-card">
            <div className="project-thumbnail">
                <img
                    src={project.thumbnail}
                    alt={project.name}
                    onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                    }}
                />
                <div className="thumbnail-placeholder" style={{ display: 'none' }}>
                    {project.name}
                </div>
            </div>
            <div className="project-content">
                <h3 className="project-name">{project.name}</h3>
                <ul className="project-description">
                    {project.description.map((point, pIndex) => (
                        <li key={pIndex}>{point}</li>
                    ))}
                </ul>
                <div className="project-tech">
                    {project.tech.map((tech, tIndex) => (
                        <span key={tIndex} className="tech-badge">{tech}</span>
                    ))}
                </div>
                <div className="project-links">
                    <a href={project.github} target="_blank" rel="noopener noreferrer" className="project-link">
                        <FaGithub /> GitHub
                    </a>
                    {project.demo && (
                        <a href={project.demo} target="_blank" rel="noopener noreferrer" className="project-link">
                            <FaExternalLinkAlt /> Demo
                        </a>
                    )}
                </div>
            </div>
        </div>
    );

    return (
        <div className="page-container projects-page">
            <Fade triggerOnce duration={800}>
                <h1 className="page-title">Projects</h1>
            </Fade>

            <Fade triggerOnce duration={600} delay={100}>
                <div className="projects-grid">
                    {projects.map((project, index) => renderProjectCard(project, index))}
                </div>
            </Fade>

            <Fade triggerOnce duration={800} delay={200}>
                <h2 className="section-heading automations-heading">
                    <FaRobot className="heading-icon" />
                    Automations
                </h2>
            </Fade>

            <Fade triggerOnce duration={600} delay={300}>
                <div className="projects-grid">
                    {automations.map((automation, index) => renderProjectCard(automation, index))}
                </div>
            </Fade>
        </div>
    );
};

export default ProjectsPage;
